/**
 * camera_fps_cap_store — 카메라별 "MQTT 로 내보낼 최대 FPS" 상한의 경량 외부 스토어.
 *
 * 콕핏은 실측 수신 FPS(useCameraReceiverFps)를 매초 MQTT 로 차량에 보고한다. 이 스토어는
 * 카메라별 상한을 담아, 전송 직전에 min(실측, 상한) 으로 깎을 수 있게 한다. 실제 차량이
 * 낮은 FPS 를 받는 상황을 실험하기 위한 개발자 도구다.
 *
 * cockpit_speed_limit_store 와 동일한 패턴(useSyncExternalStore 싱글턴) — brain-proto/mqtt
 * 같은 무거운 의존성을 끌어오지 않아 가벼운 컴포넌트/테스트에서 안전하게 공유된다.
 *
 * 상한이 설정되지 않은(=undefined) 슬롯은 깎지 않고 실측을 그대로 보낸다. 화면 표시용
 * FPS(배지/그래프)는 이 상한과 무관하게 항상 실측을 쓴다(MQTT 전송값만 영향).
 */
import { useSyncExternalStore } from "react";
import type { CameraTrackSlot } from "../../interface/webrtc_media_slots";
import type { CameraFpsMap } from "../../types/camera_fps";

export type CameraFpsCapMap = Partial<Record<CameraTrackSlot, number>>;

// 슬라이더 상한 범위 — 센서 캡처가 30fps 이므로 그 위는 의미가 없다.
export const CAMERA_FPS_CAP_LIMITS = Object.freeze({
  min: 0,
  max: 30,
  step: 1,
});

let cameraFpsCaps: CameraFpsCapMap = {};
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

/**
 * 슬롯의 상한을 설정한다. value 가 null/유한하지 않으면 상한을 해제(실측 그대로 전송).
 */
export function publishCameraFpsCap(
  slot: CameraTrackSlot,
  value: number | null,
): void {
  const next: CameraFpsCapMap = { ...cameraFpsCaps };
  if (value == null || !Number.isFinite(value)) {
    if (!(slot in next)) {
      return;
    }
    delete next[slot];
  } else {
    const clamped = Math.max(
      CAMERA_FPS_CAP_LIMITS.min,
      Math.min(CAMERA_FPS_CAP_LIMITS.max, value),
    );
    if (next[slot] === clamped) {
      return;
    }
    next[slot] = clamped;
  }
  cameraFpsCaps = next;
  emit();
}

/** 모든 상한 해제. */
export function clearAllCameraFpsCaps(): void {
  if (Object.keys(cameraFpsCaps).length === 0) {
    return;
  }
  cameraFpsCaps = {};
  emit();
}

export function getCameraFpsCapsSnapshot(): CameraFpsCapMap {
  return cameraFpsCaps;
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/**
 * 리렌더 없이 상한 변경만 받아본다 — 주행 로깅처럼 값을 기록만 하는 쪽이 무거운
 * 컴포넌트를 다시 그리게 만들지 않도록, 훅과 별개로 구독 함수를 노출한다.
 */
export function subscribeCameraFpsCaps(callback: () => void): () => void {
  return subscribe(callback);
}

/** 카메라별 상한을 구독한다(설정 변경 시 리렌더). */
export function useCameraFpsCaps(): CameraFpsCapMap {
  return useSyncExternalStore(
    subscribe,
    getCameraFpsCapsSnapshot,
    getCameraFpsCapsSnapshot,
  );
}

/**
 * 실측 FPS 맵에 상한을 적용해 "전송할" FPS 맵을 만든다(순수 함수).
 *
 * - 상한이 없는 슬롯: 실측 그대로.
 * - 상한이 있고 실측이 유한한 슬롯: min(실측, 상한).
 * - 실측이 null/undefined 인 슬롯: 측정값이 없으므로 상한과 무관하게 그대로 둔다
 *   (없는 값을 상한으로 만들어내지 않는다).
 */
export function applyCameraFpsCaps(
  measured: CameraFpsMap,
  caps: CameraFpsCapMap,
): CameraFpsMap {
  let changed = false;
  const next: CameraFpsMap = { ...measured };

  (Object.keys(caps) as CameraTrackSlot[]).forEach((slot) => {
    const cap = caps[slot];
    const value = measured[slot];
    if (
      typeof cap === "number" &&
      typeof value === "number" &&
      Number.isFinite(value) &&
      value > cap
    ) {
      next[slot] = cap;
      changed = true;
    }
  });

  return changed ? next : measured;
}
